import { errorCode } from "./companyLabels";

export const COUNTERPARTY_KIND_LABELS = {
  employee: "Співробітник",
  partner: "Партнер",
  external: "Зовнішній",
};

export const OBLIGATION_STATUS_LABELS = {
  open: "Відкрите",
  settled: "Сплачене",
  written_off: "Списане",
};

export const CORRECTION_TARGET_KIND_LABELS = {
  receipt: "Надходження",
  obligation: "Зобов'язання",
  allocation: "Розподіл коштів",
  settlement: "Виплата",
};

export const COMPANY_FINANCE_CURRENCIES = ["UAH", "USD", "EUR"];

export const counterpartyKindLabel = (kind) =>
  COUNTERPARTY_KIND_LABELS[kind] || kind || "—";

export const obligationStatusLabel = (status) =>
  OBLIGATION_STATUS_LABELS[status] || status || "—";

export const correctionTargetKindLabel = (kind) =>
  CORRECTION_TARGET_KIND_LABELS[kind] || kind || "—";

/**
 * Render server money with exactly two decimals and stored currency code.
 * No FX. Does not recompute authoritative ledger totals.
 */
export function formatCompanyFinanceMoney(amount, currency) {
  if (amount === null || amount === undefined || amount === "") {
    return "—";
  }
  const numeric = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  const absolute = Math.abs(numeric).toFixed(2);
  const [whole, fraction] = absolute.split(".");
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const signed = numeric < 0 ? `-${groupedWhole}.${fraction}` : `${groupedWhole}.${fraction}`;
  const code = currency ? String(currency) : "";
  return code ? `${signed} ${code}` : signed;
}

/** Signed correction delta: always show leading + / − with two decimals. */
export function formatCompanyFinanceDelta(amount, currency) {
  if (amount === null || amount === undefined || amount === "") {
    return "—";
  }
  const numeric = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  const absolute = Math.abs(numeric).toFixed(2);
  const [whole, fraction] = absolute.split(".");
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const sign = numeric < 0 ? "−" : "+";
  const code = currency ? String(currency) : "";
  const body = `${groupedWhole}.${fraction}`;
  return code ? `${sign}${body} ${code}` : `${sign}${body}`;
}

export function formatCompanyFinanceDate(value) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("uk-UA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function kyivTodayISO() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Kyiv",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * Group list rows by currency for display summaries.
 * Never sums across currencies.
 */
export function groupRowsByCurrency(rows, currencyKey = "currency") {
  const groups = new Map();
  (rows || []).forEach((row) => {
    const currency = row?.[currencyKey] || "—";
    if (!groups.has(currency)) groups.set(currency, []);
    groups.get(currency).push(row);
  });
  return Array.from(groups.entries()).map(([currency, items]) => ({
    currency,
    items,
  }));
}

export function mapCompanyFinanceError(err) {
  const code = errorCode(err);
  if (code === "finance_permission_required") {
    return {
      code,
      message: "У вас немає доступу до фінансів компанії.",
    };
  }
  if (code === "corporate_writes_disabled") {
    return {
      code,
      message: "Фінансові операції компанії зараз недоступні.",
    };
  }
  if (code === "finance_bound_exceeded") {
    return {
      code,
      message:
        "Операція перевищує доступний фінансовий залишок. Дані оновлено.",
      refetch: true,
      autoRetry: false,
    };
  }
  if (code === "not_company_record" || err?.response?.status === 404) {
    return {
      code: code || "not_company_record",
      message: "Запис недоступний.",
    };
  }
  return {
    code,
    message: null,
  };
}

/**
 * Books authority from current Company projection.
 * Do not infer from role alone — require effective can_access_company_finance.
 * Backend O7: ACTIVE OWNER or ACTIVE ADMIN with finance flag.
 */
export function canAccessCompanyFinanceBooks(company, membership) {
  const status =
    membership?.status || company?.my_membership?.status || null;
  if (status !== "active") return false;
  return company?.can_access_company_finance === true;
}

export function canCreateCompanyObligation(company, membership) {
  if (!canAccessCompanyFinanceBooks(company, membership)) return false;
  return company?.status === "active";
}

export function canContinueArchivedCompanyFinance(company, membership) {
  if (!canAccessCompanyFinanceBooks(company, membership)) return false;
  return company?.status === "archived";
}

export function canMutateCompanyFinance(company, membership) {
  if (!canAccessCompanyFinanceBooks(company, membership)) return false;
  return company?.status === "active" || company?.status === "archived";
}

export function membershipGenerationOf(company, membership) {
  return (
    membership?.membership_generation ??
    company?.my_membership?.membership_generation ??
    null
  );
}

export function isHistoricalMembership(company, membership) {
  const status =
    membership?.status || company?.my_membership?.status || null;
  return status === "suspended" || status === "ended";
}

export function listResults(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.results)) return payload.results;
  return [];
}
